/**
 * usePhotos Hook
 * Photo list and management operations with React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { createQueryKeys } from './queryKeys';
import type { Photo, PhotoListParams } from '../types/api';

// ============================================================================
// Query Keys
// ============================================================================

const base = createQueryKeys('photos');
export const photoKeys = {
  ...base,
  list: (policyId: string, params?: PhotoListParams) =>
    [...base.lists(), policyId, params] as const,
  byPolicy: (policyId: string) => [...base.all, 'policy', policyId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch photos for a specific policy
 */
export function usePhotos(policyId: string | undefined, params?: PhotoListParams) {
  return useQuery({
    queryKey: photoKeys.list(policyId!, params),
    queryFn: () => api.photos.list(policyId!, params),
    enabled: Boolean(policyId),
  });
}

/**
 * Fetch a single photo by ID
 */
export function usePhoto(id: string | undefined) {
  return useQuery({
    queryKey: photoKeys.detail(id!),
    queryFn: () => api.photos.get(id!),
    enabled: Boolean(id),
  });
}

/**
 * Delete a photo
 */
export function useDeletePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.photos.delete(id),
    onSuccess: () => {
      // Invalidate all photo lists
      queryClient.invalidateQueries({ queryKey: photoKeys.lists() });
    },
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if photo is "new" (uploaded in last 7 days)
 */
export function isPhotoNew(photo: Photo): boolean {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return new Date(photo.uploadedAt) > sevenDaysAgo;
}

/**
 * Check if photo has GPS data
 */
export function hasGPS(photo: Photo): boolean {
  return photo.latitude !== null && photo.longitude !== null;
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get photo type label
 */
export function getPhotoTypeLabel(type: string | null): string {
  if (!type) return 'Other';

  const labels: Record<string, string> = {
    front_exterior: 'Front Exterior',
    rear_exterior: 'Rear Exterior',
    roof: 'Roof',
    brush_clearance: 'Brush Clearance',
    access_road: 'Access Road',
    water_source: 'Water Source',
    other: 'Other',
  };

  return labels[type] || 'Other';
}

export default usePhotos;
